import { GridColDef } from '@mui/x-data-grid-pro';
import { XGridDefaultColumn } from '@AMIEWEB/Common/XGrid/cells/XGridDefaults';
import { XGridOverflowCell } from '@AMIEWEB/Common/XGrid/cells/XGridOverflowCell';
import { XGridSideBarViewCell } from '@AMIEWEB/Common/XGrid/cells/XGridSideBarViewCell';
import { Link as MuiLink } from '@mui/material';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { missingField } from 'app/constants';
import { convertToPercentage } from 'app/components/MarginTool/Components/PayPackage/helper';
import { formattedNumber } from 'app/components/MarginTool/Components/PayPackage/PayPackagePreview/helper';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { PlacementPayPackagesEmailCell } from './PlacementPayPackagesEmailCell';

const DefaultCell = {
  ...XGridDefaultColumn,
  flex: 0,
};

const asDateTimePST = (value: any) => (value ? moment(value).isValid() ? `${momentTz
                .tz(value, 'UTC')
                .tz('America/Los_Angeles')
                .format('L hh:mm A')}`: missingField : missingField);
const asDateTime = (value: any) => (value ? moment(value).isValid() ? moment(value).format('MM/DD/YYYY hh:mm:ss A') : missingField : missingField);
const asDate = (value: any) => (value ? (moment(value).isValid() ? moment(value).format('MM/DD/YYYY') : missingField) : missingField);

const formatCurrency = (value: any, options?: { showMissingWhenZero?: boolean }) => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return missingField;
  if (options?.showMissingWhenZero && !n) return missingField;

  const abs = Math.abs(n);
  const formatted = formattedNumber(abs.toFixed(2));
  const withDollar = `$${formatted}`;
  return n < 0 ? `(${withDollar})` : withDollar;
};

const OverflowFormattedCell = (getText: (value: any) => string) =>
  XGridOverflowCell()((p: any) => React.createElement('span', null, getText(p.value)));

const buildPathFromRow = (path: string, fieldList: string[], row: any) => {
  let linkBuild = path;
  fieldList?.forEach((id, index) => {
    linkBuild = linkBuild.replace(`{${index}}`, `${row?.[id]}`);
  });
  return linkBuild;
};

/**
 * Grid-local link renderer: opens in a new tab by default.
 * Kept local so we don't change link behavior in other grids.
 */
const XGridNewTabLinkCell =
  (path: string, fieldList: string[] = []) =>
  (params: any) => {
    if (params?.value === missingField) return React.createElement('span', null, params?.value);
    const to = buildPathFromRow(path, fieldList, params?.row);
    return React.createElement(
      MuiLink,
      {
        component: RouterLink as any,
        to,
        target: '_blank',
        rel: 'noopener noreferrer',
        underline: 'hover',
      } as any,
      params?.value ?? missingField,
    );
  };

export const placementPayPackagesColumns = (
  t,
  opts?: { onSelectEmailVariant?: (row: any, variant: string) => void; emailDisabled?: boolean },
): GridColDef[] => [
  {
    ...DefaultCell,
    field: 'emailIcon',
    headerName: '',
    width: 44,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    hideable: false,
    resizable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) =>
      React.createElement(PlacementPayPackagesEmailCell, {
        disabled: opts?.emailDisabled,
        onSelectVariant: (variant: string) => opts?.onSelectEmailVariant?.(params?.row, variant),
      }),
  },
  {
    ...DefaultCell,
    field: 'placementId',
    headerName: 'Placement ID',
    width: 110,
    renderCell: XGridOverflowCell(XGridNewTabLinkCell('/placement/{0}', ['placementId']))(
      XGridNewTabLinkCell('/placement/{0}', ['placementId']),
    ),
  },
  {
    ...DefaultCell,
    field: 'orderId',
    headerName: 'Order ID',
    width: 100,
    valueGetter: params =>
      params.row?.orderId ?? params.row?.orderID ?? params.row?.order?.orderId ?? params.value,
    renderCell: XGridOverflowCell(
      XGridNewTabLinkCell('/order/{0}?tab=6', ['orderId']),
    )(XGridNewTabLinkCell('/order/{0}?tab=6', ['orderId'])),
  },
  {
    ...DefaultCell,
    field: 'placementStatus',
    headerName: 'Placement Status',
    width: 160,
    renderCell: params => params.value ?? missingField,
  },
  {
    ...DefaultCell,
    field: 'placementStartDate',
    headerName: 'Placement Start Date',
    width: 150,
    renderCell: params => asDate(params.value),
  },
  {
    ...DefaultCell,
    field: 'placementEndDate',
    headerName: 'Placement End Date',
    width: 150,
    renderCell: params => asDate(params.value),
  },
  {
    ...DefaultCell,
    field: 'skillSet',
    headerName: 'Skill Set',
    width: 220,
  },
  {
    ...DefaultCell,
    field: 'scenarioStatus',
    headerName: 'Pay Package Status',
    width: 170,
  },
  {
    ...DefaultCell,
    field: 'grossMargin',
    headerName: 'Gross Margin%',
    width: 120,
    renderCell: OverflowFormattedCell(value => (value === null || value === undefined ? missingField : convertToPercentage(Number(value)))),
  },
  {
    ...DefaultCell,
    field: 'grossProfit',
    headerName: 'Gross Profit',
    width: 120,
    renderCell: OverflowFormattedCell(value => formatCurrency(value, { showMissingWhenZero: true })),
  },
  {
    ...DefaultCell,
    field: 'negotiatedContributionMargin',
    headerName: 'NCM',
    width: 120,
    renderCell: OverflowFormattedCell(value => formatCurrency(value, { showMissingWhenZero: true })),
  },
  {
    ...DefaultCell,
    field: 'weeklyGrossPay',
    headerName: 'Weekly Pay - Total',
    width: 150,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'travelerId',
    headerName: 'Candidate ID',
    width: 120,
    renderCell: XGridOverflowCell(XGridSideBarViewCell('/candidate/{0}/{1}', ['travelerId', 'brandId'], true))(
      XGridSideBarViewCell('/candidate/{0}/{1}', ['travelerId', 'brandId']),
    ),
  },
  {
    ...DefaultCell,
    field: 'candidateName',
    headerName: 'Candidate Name',
    width: 180,
    renderCell: XGridOverflowCell(XGridNewTabLinkCell('/candidate/{0}/{1}', ['travelerId', 'brandId']))(
      XGridNewTabLinkCell('/candidate/{0}/{1}', ['travelerId', 'brandId']),
    ),
  },
  {
    ...DefaultCell,
    field: 'packageName',
    headerName: 'Booking Period Name',
    width: 170,
  },
  {
    ...DefaultCell,
    field: 'bookingPeriodStartDate',
    headerName: 'Booking Period Start Date',
    width: 170,
    renderCell: params => asDate(params.value),
  },
  {
    ...DefaultCell,
    field: 'bookingPeriodEndDate',
    headerName: 'Booking Period End Date',
    width: 170,
    renderCell: params => asDate(params.value),
  },
  {
    ...DefaultCell,
    field: 'bookingPeriodDuration',
    headerName: 'Booking Period Duration (Weeks)',
    width: 220,
    valueFormatter: params => params.value ?? missingField,
  },
  {
    ...DefaultCell,
    field: 'tla',
    headerName: 'TLA',
    width: 80,
    renderCell: params => (params.value === true ? 'Yes' : params.value === false ? 'No' : missingField),
    valueFormatter: params => (params.value === true ? 'Yes' : params.value === false ? 'No' : missingField),
  },
  {
    ...DefaultCell,
    field: 'scenarioId',
    headerName: 'Scenario ID',
    width: 110,
    renderCell: XGridOverflowCell(
      XGridNewTabLinkCell('/margin/placement/{0}/{1}', ['placementId', 'scenarioId']),
    )(XGridNewTabLinkCell('/margin/placement/{0}/{1}', ['placementId', 'scenarioId'])),
  },
  {
    ...DefaultCell,
    field: 'scenarioName',
    headerName: 'Scenario Name',
    width: 160,
    renderCell: XGridOverflowCell(
      XGridNewTabLinkCell('/margin/placement/{0}/{1}', ['placementId', 'scenarioId']),
    )(XGridNewTabLinkCell('/margin/placement/{0}/{1}', ['placementId', 'scenarioId'])),
  },
  {
    ...DefaultCell,
    field: 'createdDateAt',
    headerName: 'Created Date',
    width: 180,
    renderCell: params => asDateTimePST(params.value),
  },
  {
    ...DefaultCell,
    field: 'createdByName',
    headerName: 'Created By',
    width: 170,
  },
  {
    ...DefaultCell,
    field: 'approvedDateAt',
    headerName: 'Approved Date',
    width: 180,
    renderCell: params => asDateTimePST(params.value),
  },
  {
    ...DefaultCell,
    field: 'approvedByName',
    headerName: 'Approved By',
    width: 170,
  },
  {
    ...DefaultCell,
    field: 'verifiedDateAt',
    headerName: 'Verified Date',
    width: 180,
    renderCell: params => asDateTimePST(params.value),
  },
  {
    ...DefaultCell,
    field: 'verifiedByName',
    headerName: 'Verified By',
    width: 170,
  },
  {
    ...DefaultCell,
    field: 'numberOfSplits',
    headerName: 'Number of Splits',
    width: 150,
  },
  {
    ...DefaultCell,
    field: 'totalBonuses',
    headerName: 'Total Bonuses',
    width: 140,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'totalReimbursements',
    headerName: 'Total Reimbursements',
    width: 170,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'splitId',
    headerName: 'Split ID',
    width: 110,
  },
  {
    ...DefaultCell,
    field: 'splitStartDate',
    headerName: 'Split Start Date',
    width: 150,
    renderCell: params => asDate(params.value),
  },
  {
    ...DefaultCell,
    field: 'splitEndDate',
    headerName: 'Split End Date',
    width: 150,
    renderCell: params => asDate(params.value),
  },
  {
    ...DefaultCell,
    field: 'weeklyGrossPaySplit',
    headerName: 'Weekly Pay - Split',
    width: 150,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'billRate',
    headerName: 'Bill Rate',
    width: 110,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'vms',
    headerName: 'VMS Fees',
    width: 110,
    renderCell: OverflowFormattedCell(value => (value == null ? missingField : `${Number(value).toFixed(2)}%`)),
  },
  {
    ...DefaultCell,
    field: 'other',
    headerName: 'Other Fees',
    width: 110,
    renderCell: OverflowFormattedCell(value => (value == null ? missingField : `${Number(value).toFixed(2)}%`)),
  },
  {
    ...DefaultCell,
    field: 'payRate',
    headerName: 'Pay Rate',
    width: 110,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'shift',
    headerName: 'Shift',
    width: 110,
  },
  {
    ...DefaultCell,
    field: 'hoursPerWeek',
    headerName: 'Hours Per Week',
    width: 140,
  },
  {
    ...DefaultCell,
    field: 'hoursPerShift',
    headerName: 'Hours Per Shift',
    width: 140,
  },
  {
    ...DefaultCell,
    field: 'lodgingPerDiem',
    headerName: 'Lodging Per Diem',
    width: 160,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'mealPerDiem',
    headerName: 'Meal Per Diem',
    width: 150,
    renderCell: OverflowFormattedCell(value => formatCurrency(value)),
  },
  {
    ...DefaultCell,
    field: 'housingType',
    headerName: 'Housing Type',
    width: 150,
    valueFormatter: params => params.value ?? missingField,
  },
  {
    ...DefaultCell,
    field: 'updatedDateAt',
    headerName: 'Last Updated Date',
    width: 180,
    renderCell: params => asDateTimePST(params.value),
  },
  {
    ...DefaultCell,
    field: 'updatedByName',
    headerName: 'Last Updated By',
    width: 170,
  },
];

export const defaultPinnedColumns = {
  left: ['emailIcon'],
  right: [],
};

// Default visibility per requirements (true = default column)
export const placementPayPackagesColumnVisibility = {
  emailIcon: true,
  // Defaults exactly per requirement
  placementId: true,
  orderId: true,
  placementStatus: false,
  placementStartDate: false,
  placementEndDate: false,
  skillSet: true,
  scenarioStatus: true,
  grossMargin: true,
  grossProfit: false,
  negotiatedContributionMargin: false,
  weeklyGrossPay: true,
  travelerId: false,
  candidateName: false,
  packageName: true,
  bookingPeriodStartDate: true,
  bookingPeriodEndDate: true,
  bookingPeriodDuration: true,
  tla: false,
  scenarioId: true,
  scenarioName: true,
  createdDateAt: false,
  createdByName: false,
  approvedDateAt: false,
  approvedByName: false,
  verifiedDateAt: false,
  verifiedByName: false,
  numberOfSplits: true,
  totalBonuses: true,
  totalReimbursements: true,
  splitId: true,
  splitStartDate: true,
  splitEndDate: true,
  weeklyGrossPaySplit: true,
  billRate: true,
  vms: false,
  other: false,
  payRate: true,
  shift: true,
  hoursPerWeek: false,
  hoursPerShift: false,
  lodgingPerDiem: true,
  mealPerDiem: true,
  housingType: false,
  updatedDateAt: true,
  updatedByName: true,
};

